// FinancialHealthScoreDetailsPDF.jsx
import {
  Font,
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import DejaVuSans from "../../../../../../assets/images/pdf/FHS_details/dejavu-lgc-fonts-ttf-2.37/ttf/DejaVuLGCSans.ttf";
import PoppinsRegular from "../../../../../../assets/images/pdf/FHS_details/Poppins/Poppins-Regular.ttf";
import PoppinsBold from "../../../../../../assets/images/pdf/FHS_details/Poppins/Poppins-Bold.ttf";

import React from "react";
import exportimages from "../../../../../../assets/images/exportImages";
import {
  formatToDollar,
  getArrowIndicatorBase64Image,
} from "../../../../../../utils/Utils";
import { loadImageAsBase64 } from "./PDFUtils";
import PropTypes from "prop-types";

Font.register({
  family: "DejaVuSans",
  src: DejaVuSans,
});

Font.register({
  family: "Poppins",
  fonts: [
    { src: PoppinsRegular, fontWeight: 400 },
    { src: PoppinsBold, fontWeight: 700 },
  ],
});

export const PDFstyles = StyleSheet.create({
  normal: {
    fontFamily: "Poppins",
    fontWeight: 400,
  },
  bold: {
    fontFamily: "Poppins",
    fontWeight: 700,
  },
  semibold: {
    fontFamily: "Poppins",
    fontWeight: 600,
  },
  page: {
    padding: 30,
    fontSize: 10,
    lineHeight: 1.5,
    color: "#1a1a1a",
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  headerSection: { margin: "30px 70px 10px 70px", minHeight: "210px" },
  title: {
    fontFamily: "Poppins",
    fontSize: "32px",
    fontWeight: "700",
    color: "#000",
    lineHeight: "46px",
  },
  subtitle: {
    fontFamily: "Poppins",
    fontSize: "18px",
    fontWeight: "500",
    marginTop: "10px",
  },
  formattedDate: {
    fontFamily: "Poppins",
    fontSize: "12px",
    fontWeight: "400",
    color: "#4b4b4b",
    marginBottom: "6px",
  },
  downloadPdfDesc: {
    fontFamily: "Poppins",
    fontSize: "9px",
    fontWeight: "400",
    lineHeight: "12px",
    color: "#868687",
  },
  insidePage: { padding: "32px 70px" },
  pageOneCard: {
    padding: "16px 20px 16px 20px",
    backgroundColor: "#F0F2FC",
    borderRadius: "6px",
    marginBottom: "16px",
    // height: "250px",
  },
  pageOneTitle: {
    fontFamily: "Poppins",
    fontSize: "14px",
    fontWeight: "600",
    lineHeight: "100%",
    color: "#000",
    marginBottom: "20px",
  },
  pageOneCardTitle: {
    fontFamily: "Poppins",
    fontSize: "14px",
    fontWeight: "600",
    lineHeight: "18px",
    color: "#000",
    marginBottom: "6px",
  },
  pageOneCardDesc: {
    fontFamily: "Poppins",
    fontSize: "10px",
    fontWeight: "400",
    lineHeight: "16px",
    color: "#494949",
  },
  cardContainer: {
    flexDirection: "column",
    gap: 10,
  },
  pageOneWhiteCard: {
    width: "48%", // This ensures two cards per row
    backgroundColor: "#fff",
    borderRadius: 6,
    padding: "16px",
    border: "1px solid #E4EFFF",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "158px",
  },
  pageOneCardSubTitle: {
    fontFamily: "Poppins",
    fontSize: "14px",
    fontWeight: "600",
    lineHeight: "18px",
    color: "#181D27",
    marginBottom: "3px",
  },
  pageOneCardSubDesc: {
    fontFamily: "Poppins",
    fontSize: "10px",
    fontWeight: "400",
    lineHeight: "14px",
    color: "#4b4b4b",
    // marginBottom: "10px",
  },
  pageOneCardValues: {
    fontFamily: "Poppins",
    marginTop: "auto", // pushes this Text to the bottom
    fontSize: "12px",
    fontWeight: "600",
    lineHeight: "18px",
    color: "#000",
  },
  cohortAverage: {
    fontFamily: "Poppins",
    fontSize: "10px",
    fontWeight: "400",
    borderRadius: "4px",
    width: "100%",
    paddingVertical: 4,
    paddingHorizontal: 4,
    textAlign: "center",
  },
  pageFooter: {
    position: "absolute",
    bottom: 0,
    left: 50,
    right: 50,
    flexDirection: "row",
    justifyContent: "space-between",
    fontFamily: "Poppins",
    fontSize: "9px",
    fontWeight: "500",
    color: "#222",
    borderTop: "1px solid #E6E6FF",
    padding: "6px 20px 13px 20px",
  },
  pageNumber: {
    fontFamily: "Poppins",
    fontSize: "9px",
    fontWeight: "400",
    color: "#868687",
  },
  allSegmentContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: "24px",
    backgroundColor: "#F8F8F8",
    borderRadius: "10px",
  },
  allSegmentCard: {
    padding: "16px",
    width: "50%", // leave room for spacing
    flexDirection: "column",
    position: "relative",
    height: "194px",
  },
  verticalSeparator: {
    width: 1,
    backgroundColor: "#ECECEC",
    height: "85%",
    alignSelf: "center",
  },
  icon: {
    padding: 8,
    backgroundColor: "#6c7de5",
    height: "36px",
    width: "36px",
    border: "2px solid #6c7de5",
    borderRadius: "6px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: "8px",
  },
  segmentIcon: {
    padding: 8,
    backgroundColor: "#fff",
    height: "36px",
    width: "36px",
    border: "1px solid #BBC3F3",
    borderRadius: "6px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: "8px",
  },
  logoImage: {
    width: "102px",
    height: "36px",
    marginBottom: "15px",
  },
  badgesRow: {
    flexDirection: "row",
    gap: 6,
  },
  badge: {
    fontFamily: "Poppins",
    fontSize: "11px",
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 3,
  },

  arrowIconContainer: {
    width: 40,
    height: 40, // ❗ Add height to properly center vertically
    display: "flex",
    flexDirection: "row", // default, but okay to include
    justifyContent: "center", // ⬇ Center vertically
    alignItems: "center", // ⬅ Center horizontally
  },
  arrowIcon: {
    width: 10,
    height: 10,
    marginRight: 4,
  },
  EWIPageTitle: {
    fontFamily: "Poppins",
    fontSize: "14px",
    fontWeight: "500",
    lineHeight: "14px",
    color: "#676767",
    marginBottom: "12px",
  },
  EWItitle: {
    fontFamily: "Poppins",
    fontSize: "18px",
    fontWeight: "600",
    lineHeight: "26px",
    color: "#000",
  },
  EWICardData: {
    fontFamily: "Poppins",
    fontSize: "11px",
    fontWeight: "400",
    lineHeight: "18px",
    color: "#535862",
  },
  lightBlueCard: {
    backgroundColor: "#F0F7FF",
    borderRadius: "6px",
    padding: "24px 24px 12px 24px",
    marginBottom: "36px",
  },
});

const icons = {
  green_upward_arrow: loadImageAsBase64(exportimages.green_upward_arrow),
  green_downward_arrow: loadImageAsBase64(exportimages.green_downward_arrow),
  orange_upward_arrow: loadImageAsBase64(exportimages.orange_upward_arrow),
  orange_downward_arrow: loadImageAsBase64(exportimages.orange_downward_arrow),
  red_upward_arrow: loadImageAsBase64(exportimages.red_upward_arrow),
  red_downward_arrow: loadImageAsBase64(exportimages.red_downward_arrow),
};

export const chunkArray = (arr, size) =>
  Array.from({ length: Math.ceil(arr?.length / size) }, (_, i) =>
    arr?.slice(i * size, i * size + size),
  );

export const cleanText = (text) => {
  if (!text) return text;

  let cleaned = text
    .normalize("NFKC")

    // ✅ normalize all hyphen-like chars → standard hyphen
    .replaceAll(/[\u2010\u2011\u2012\u2013\u2014\u2015]/g, "-")

    .replaceAll("\u00a0", " ")
    .replaceAll("\uFFFD", "")
    .replaceAll(/[^\x20-\x7E\n]/g, "");

  // FIX malformed links like ((text](url))
  cleaned = cleaned.replaceAll(
    /\(\(\s*([^\]\n]{1,200}?)\]\(([^)\n]{1,500}?)\)\s*\)\)/g,
    "([$1]($2))",
  );

  // Ensure space before '(' if missing
  cleaned = cleaned.replaceAll(/([^\s])\(/g, "$1 (");

  return cleaned.trim();
};

const highlightTitles = new Set([
  "recommendation",
  "expected outcome with autopilot",
]);

export const isHighlighted = (title) => {
  const normalized = cleanText(title).toLowerCase().trim();
  return highlightTitles.has(normalized);
};

export const hyphenationCallback = (word) => {
  if (word.length > 25 || word.includes("http") || word.includes("www")) {
    return word.split(/([/.?&=_-])/g);
  }
  return [word];
};

export const breakLongUrl = (text) => {
  return text.replace(
    /(https?:\/\/[^\s)]+)/g,
    (url) => {
      return url.replace(
        /([A-Za-z0-9%]{35,})/g,
        (longToken) =>
          longToken.match(/.{1,20}/g).join(" ")
      );
    }
  );
};


export const PDFFooter = ({ style }) => (
  <View fixed style={style}>
    <Text>Autopilot</Text>
    <Text
      render={({ pageNumber }) =>
        pageNumber < 10 ? `0${pageNumber}` : `${pageNumber}`
      }
      style={PDFstyles.pageNumber}
    />
  </View>
);

PDFFooter.propTypes = {
  style: PropTypes.object,
};

export const MainPage = ({
  base64Logo,
  base64Banner,
  orgName,
  formattedDate,
  selectedQuater,
}) => (
  <View>
    <View style={PDFstyles.headerSection}>
      {base64Logo && <Image src={base64Logo} style={PDFstyles.logoImage} />}
      {/* <Text style={PDFstyles.title}>Financial Health</Text>
        <Text style={PDFstyles.title}>Report</Text> */}
      <Text style={PDFstyles.title}>
        Financial Performance Benchmark Report &nbsp;
        <Text style={{ fontWeight: "normal" }}>|&nbsp;{selectedQuater}</Text>
      </Text>
      <Text style={PDFstyles.subtitle}>{orgName}</Text>
    </View>

    <View>{base64Banner && <Image src={base64Banner} />}</View>

    <View style={{ marginTop: 10, marginHorizontal: 70, marginBottom: 0 }}>
      <Text style={PDFstyles.formattedDate}>{formattedDate}</Text>
      <Text style={PDFstyles.downloadPdfDesc}>
        This report is for informational purposes only and is intended for the
        authorized recipient. {orgName} is not responsible for errors,
        omissions, or decisions based on this report. Unauthorized use or
        distribution is prohibited. For verification, contact sales@bond.ai
      </Text>
    </View>
  </View>
);

MainPage.propTypes = {
  base64Logo: PropTypes.string,
  base64Banner: PropTypes.string,
  orgName: PropTypes.string,
  formattedDate: PropTypes.string,
  selectedQuater: PropTypes.string,
};

export const FHSGuageChart = ({ base64Chart }) => (
  <View
    style={[
      PDFstyles.pageOneCard,
      {
        marginTop: "10px",
        maxWidth: "100%",
        width: "auto",
        height: "auto",
        objectFit: "cover",
      },
    ]}
  >
    {/* <Text style={PDFstyles.pageOneCardTitle}>Financial Health</Text> */}
    <Text style={PDFstyles.pageOneCardTitle}>
      Financial Performance Benchmark
    </Text>
    <Text style={PDFstyles.pageOneCardDesc}>
      Overall strength of your institution based on your financial performance
      as compared to your cohort.
    </Text>

    {base64Chart && (
      <Image
        src={base64Chart}
        style={{
          maxWidth: "100%",
          width: "auto",
          height: "auto",
          objectFit: "cover",
        }}
      />
    )}
  </View>
);

FHSGuageChart.propTypes = {
  base64Chart: PropTypes.string,
};

export const NIIGraph = ({ base64ChartNII }) => (
  <View
    style={[
      PDFstyles.pageOneCard,
      {
        marginTop: "10px",
        // marginBottom: "80px",
        maxWidth: "100%",
        width: "auto",
        height: "auto",
        objectFit: "cover",
      },
    ]}
  >
    <Text style={PDFstyles.pageOneCardTitle}>Net Interest Income Forecast</Text>
    <Text style={PDFstyles.pageOneCardDesc}>
      Difference between interest revenues and interest expenses.
    </Text>
    {base64ChartNII && (
      <Image
        src={base64ChartNII}
        style={{
          maxWidth: "100%",
          width: "auto",
          height: "auto",
          objectFit: "cover",
        }}
      />
    )}
  </View>
);

NIIGraph.propTypes = {
  base64ChartNII: PropTypes.string,
};

export const SegmentInfluence = ({
  segmentInfluencingObj,
  selectedbankMetrics,
  showAPValue,
}) => (
  <View style={{ marginTop: "20px", marginBottom: "36px" }} wrap={false}>
    <Text
      style={[PDFstyles.pageOneCardTitle, { marginBottom: "16px" }]}
    >{`Institution Growth Forecast for ${selectedbankMetrics?.selectedBankMetricData?.forecast_quarter
      }`}</Text>
    <View style={PDFstyles.cardContainer}>
      {chunkArray(segmentInfluencingObj, 2)?.map((row) => {
        return (
          <View key="segmentObj" wrap={false}>
            {/* Render title only once, and only for the first block on the new page */}
            <View key="segmentObj" style={PDFstyles.row} wrap={false}>
              {row?.map((segment) => {
                const isForcasted_icon = getArrowIndicatorBase64Image(
                  "isForcasted",
                  showAPValue,
                  segment,
                );

                const isAPForcasted_icon = getArrowIndicatorBase64Image(
                  "isAPForcasted",
                  showAPValue,
                  segment,
                );

                return (
                  <View key={segment?.title} style={PDFstyles.pageOneWhiteCard}>
                    {/* Top + Middle content wrapper */}
                    <View style={{ flexGrow: 1 }}>
                      {/* Top Content */}
                      <View
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "flex-start",
                        }}
                      >
                        <View style={{ flex: 1 }}>
                          <Text style={PDFstyles.pageOneCardSubTitle}>
                            {segment?.title}
                          </Text>
                          <Text
                            style={[
                              PDFstyles.pageOneCardSubDesc,
                              { width: "75%" },
                            ]}
                          >
                            {segment?.description}
                          </Text>
                        </View>
                        <View style={[PDFstyles.icon, { marginLeft: 8 }]}>
                          <Image
                            src={segment?.base64Icon}
                            style={{ height: "17px", width: "18px" }}
                          />
                        </View>
                      </View>
                    </View>

                    {/* Bottom Content */}
                    <View style={{ marginTop: "10px" }}>
                      <Text style={PDFstyles.pageOneCardValues}>
                        {segment?.symbol === "$" && segment?.symbol}
                        {segment?.symbol === "$" &&
                          formatToDollar(segment.current)}
                        {segment?.symbol === "%" &&
                          Number(segment.value).toFixed(2)}
                        {segment?.symbol === "%" && segment?.symbol}
                      </Text>
                      <View
                        style={{
                          flexDirection: "row",
                          columnGap: 8,
                          position: "relative",
                        }}
                      >
                        {/* Forecasted Value */}
                        <View style={PDFstyles.badgesRow}>
                          <View
                            style={[
                              PDFstyles.badge,
                              {
                                backgroundColor: "#E6E6E6",
                              },
                            ]}
                          >
                            {/* <Text>{isForcasted_icon}</Text> */}
                            {icons[isForcasted_icon] && (
                              <Image
                                src={icons[isForcasted_icon]}
                                style={PDFstyles.arrowIcon}
                              />
                            )}
                            <Text>
                              {segment?.symbol === "$" && segment?.symbol}
                              {segment?.symbol === "$" &&
                                formatToDollar(segment.forecasted)}
                              {segment?.symbol === "%" &&
                                Number(segment.ap_used).toFixed(2)}
                              {segment?.symbol === "%" && segment?.symbol}
                            </Text>
                          </View>
                        </View>

                        {/* AP Used Value */}
                        {showAPValue && (
                          <View style={PDFstyles.badgesRow}>
                            <View
                              style={[
                                PDFstyles.badge,
                                {
                                  backgroundColor: "#EBF3FE",
                                },
                              ]}
                            >
                              {/* <Text>{isAPForcasted_icon}</Text> */}
                              {icons[isAPForcasted_icon] && (
                                <Image
                                  src={icons[isAPForcasted_icon]}
                                  style={PDFstyles.arrowIcon}
                                />
                              )}
                              <Text>
                                {segment?.symbol === "$" && segment?.symbol}
                                {segment?.symbol === "$" &&
                                  formatToDollar(segment?.final_AP_value)}
                                {segment?.symbol === "%" &&
                                  Number(segment?.final_AP_value).toFixed(2)}
                                {segment?.symbol === "%" && segment?.symbol}
                              </Text>
                            </View>
                          </View>
                        )}
                      </View>
                    </View>
                  </View>
                );
              })}
            </View>
          </View>
        );
      })}
    </View>
  </View>
);

SegmentInfluence.propTypes = {
  segmentInfluencingObj: PropTypes.arrayOf(PropTypes.object),
  selectedbankMetrics: PropTypes.arrayOf(PropTypes.object),
  showAPValue: PropTypes.bool,
};

const SegmentSection = ({
  title,
  segments,
  selectedbankMetrics,
  showAPValue,
}) => (
  <View style={{ marginBottom: 80 }}>
    <View>
      <Text style={PDFstyles.pageOneTitle}>
        {`${title} ${selectedbankMetrics?.selectedBankMetricData?.forecast_quarter
          }`}
      </Text>
    </View>

    {chunkArray(segments, 2)?.map((segmentRow, rowIndex) => (
      <View
        key={`segmentRow`}
        style={PDFstyles.allSegmentContainer}
        wrap={false}
      >
        {segmentRow?.map((segment, index) => {
          const isForcasted_icon = getArrowIndicatorBase64Image(
            "isForcasted",
            showAPValue,
            segment,
          );

          const isAPForcasted_icon = getArrowIndicatorBase64Image(
            "isAPForcasted",
            showAPValue,
            segment,
          );

          const isLeftCard = index === 0;

          return (
            <React.Fragment key={segment?.title}>
              <View
                style={{
                  ...PDFstyles.allSegmentCard,
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                {/* Top */}
                <View>
                  <View style={PDFstyles.segmentIcon}>
                    <Image
                      src={segment?.base64Icon}
                      style={{ height: "18px", width: "17px" }}
                    />
                  </View>
                  <View>
                    <Text style={PDFstyles.pageOneCardSubTitle}>
                      {segment?.title}
                    </Text>
                    <Text style={PDFstyles.pageOneCardSubDesc}>
                      {segment?.description}
                    </Text>
                  </View>
                </View>

                {/* Bottom */}
                <View style={{ marginTop: 8 }}>
                  <Text style={PDFstyles.pageOneCardValues}>
                    {segment?.symbol === "$" && segment?.symbol}
                    {segment?.symbol === "$" &&
                      formatToDollar(segment?.current)}
                    {segment?.symbol === "%" &&
                      Number.isFinite(Number(segment?.current))
                      ? Number(segment?.current).toFixed(2)
                      : ""}
                    {segment?.symbol === "%" && segment?.symbol}
                  </Text>

                  <View style={{ flexDirection: "row", columnGap: 8 }}>
                    {/* Forecast */}
                    <View style={PDFstyles.badgesRow}>
                      <View
                        style={[
                          PDFstyles.badge,
                          { backgroundColor: "#E6E6E6" },
                        ]}
                      >
                        {icons[isForcasted_icon] && (
                          <Image
                            src={icons[isForcasted_icon]}
                            style={PDFstyles.arrowIcon}
                          />
                        )}
                        <Text>
                          {segment?.symbol === "$" && segment?.symbol}
                          {segment?.symbol === "$" &&
                            formatToDollar(segment?.forecasted || 0)}
                          {segment?.symbol === "%" &&
                            Number.isFinite(Number(segment?.forecasted))
                            ? Number(segment?.forecasted).toFixed(2)
                            : ""}
                          {segment?.symbol === "%" && segment?.symbol}
                        </Text>
                      </View>
                    </View>

                    {/* AP Value */}
                    {showAPValue && (
                      <View style={PDFstyles.badgesRow}>
                        <View
                          style={[
                            PDFstyles.badge,
                            { backgroundColor: "#EBF3FE" },
                          ]}
                        >
                          {icons[isAPForcasted_icon] && (
                            <Image
                              src={icons[isAPForcasted_icon]}
                              style={PDFstyles.arrowIcon}
                            />
                          )}
                          <Text>
                            {segment?.symbol === "$" && segment?.symbol}
                            {segment?.symbol === "$" &&
                              formatToDollar(segment?.final_AP_value)}
                            {segment?.symbol === "%" &&
                              Number.isFinite(Number(segment?.final_AP_value))
                              ? Number(segment?.final_AP_value).toFixed(2)
                              : ""}
                            {segment?.symbol === "%" && segment?.symbol}
                          </Text>
                        </View>
                      </View>
                    )}
                  </View>
                </View>
              </View>

              {isLeftCard && segmentRow?.length === 2 && (
                <View style={PDFstyles.verticalSeparator} />
              )}
            </React.Fragment>
          );
        })}
      </View>
    ))}
  </View>
);

SegmentSection.propTypes = {
  title: PropTypes.string.isRequired,
  segments: PropTypes.arrayOf(PropTypes.object),
  selectedbankMetrics: PropTypes.shape({
    selectedBankMetricData: PropTypes.shape({
      forecast_quarter: PropTypes.string,
    }),
  }),
  showAPValue: PropTypes.bool,
};

export const AllSegment = (props) => (
  <SegmentSection
    {...props}
    title="Profitability & Risk Outlook for"
    segments={props.allSegments}
  />
);

AllSegment.propTypes = {
  allSegments: PropTypes.array,
  selectedbankMetrics: PropTypes.arrayOf(PropTypes.object),
  showAPValue: PropTypes.bool,
};

export const RegulatorySegment = (props) => (
  <SegmentSection
    {...props}
    title="Additional Metrics for"
    segments={props.regulatorySegments}
  />
);

RegulatorySegment.propTypes = {
  regulatorySegments: PropTypes.array,
  selectedbankMetrics: PropTypes.arrayOf(PropTypes.object),
  showAPValue: PropTypes.bool,
};

export const EWIData = ({ EWIdataArr }) => (
  <>
    {EWIdataArr?.map((row) => (
      <>
        <Page key="EWIDataArr" size="LETTER" style={PDFstyles.insidePage}>
          <View
            style={{
              // display: "flex",
              flexDirection: "row",
              alignItems: "center",
              marginBottom: "37px",
            }}
          >
            <View style={{ flex: 1 }}>
              <Text style={PDFstyles.EWIPageTitle}>Early Warning</Text>
              <Text style={PDFstyles.EWItitle}>{row.title_name}</Text>
            </View>
            <View>
              <Image
                src={row?.base64Icon}
                style={{ height: "82px", width: "82px" }}
              />
            </View>
          </View>

          {/* Problem, Rationale and Recommendation */}
          <EWIDataInLightBlueCard data={row} />

          <PDFFooter style={PDFstyles.pageFooter} />
        </Page>

        {/* Detailed Action Plan */}
        <EWIDataInActionPlan
          // key={rowIndex}
          data={row}
          pageStyle={PDFstyles.insidePage}
          footerStyle={PDFstyles.pageFooter}
        />
      </>
    ))}
  </>
);

EWIData.propTypes = {
  EWIdataArr: PropTypes.array,
};

export const SectionBlock = ({ title, items, icon }) => (
  <View
    style={[
      PDFstyles.lightBlueCard,
      {
        width: "100%", // FORCE FULL WIDTH
        alignSelf: "stretch",
      },
    ]}
  >
    {/* Header */}
    <View
      style={[
        {
          flexDirection: "row",
          alignItems: "center",
          width: "100%", // FULL WIDTH
          marginBottom: 12,
        },
        isHighlighted(title) && {
          backgroundColor: "#6C7DE5",
          paddingVertical: 8,
          paddingHorizontal: 12,
          borderRadius: 10,
        },
      ]}
    >
      {/* LEFT TEXT (takes all space) */}
      <Text
        style={[
          PDFstyles.pageOneCardSubTitle,
          {
            flexGrow: 1, // take remaining width
            marginBottom: 0,
            color: isHighlighted(title) ? "#fff" : "#181D27",
          },
        ]}
      >
        {cleanText(title)}
      </Text>

      {/* RIGHT ICON (fixed position) */}
      <Image
        src={icon}
        style={{
          width: 32,
          height: 32,
          marginLeft: 8, // spacing from text
          flexShrink: 0, //  don't shrink
        }}
      />
    </View>

    {/* Content */}
    {(Array.isArray(items) ? items : [items]).map((point, index) => {
      const text = cleanText(String(point).trim());

      // Split lines on 

      const lines = text.split(/<br\s*\/?>|<\/br>/gi);

      return (
        <View key="SectionBlock" style={{ marginBottom: 8 }}>
          {lines.map((line, lineIndex) => {
            const trimmed = line.trim();

            const isBullet = trimmed.startsWith("- ");
            const cleanLine = isBullet
              ? trimmed.replace(/^-+\s*/, "")
              : trimmed;

            const parts = cleanLine.split(/(\*\*.*?\*\*)/g).filter(Boolean);

            return (
              <View key={`${index}-${lineIndex}`} style={{ marginBottom: 4 }}>
                <Text
                  style={[
                    PDFstyles.EWICardData,
                    {
                      width: "100%",
                      lineHeight: 1.5,
                    },
                  ]}
                >
                  {isBullet && (
                    <Text
                      style={{
                        width: 14,
                        fontSize: 14,
                        marginTop: 1,
                        fontFamily: "Poppins",
                      }}
                    >
                      •{" "}
                    </Text>
                  )}

                  {parts.map((part, i) => {
                    const isBold = part.startsWith("**") && part.endsWith("**");

                    const content = isBold
                      ? part.replace(/^\*\*|\*\*$/g, "")
                      : part;

                    return (
                      <Text
                        key={`${index}-${lineIndex}-${i}`}
                        style={{
                          fontFamily: "Poppins",
                          fontWeight: isBold ? 700 : 400,
                        }}
                      >
                        {cleanText(content)}
                      </Text>
                    );
                  })}
                </Text>
              </View>
            );
          })}
        </View>
      );
    })}
  </View>
);

SectionBlock.propTypes = {
  title: PropTypes.string,
  items: PropTypes.array,
  icon: PropTypes.node,
};

export const EWIDataInLightBlueCard = ({ data }) => {
  if (!data.problem?.length) return null;

  return (
    <>
      <View wrap={false}>
        <SectionBlock
          title="Problem"
          items={data.problem_summary}
          icon={data.problem_icon}
        />
      </View>
      <View wrap={false}>
        <SectionBlock
          title="Consequence"
          items={data.rationale_summary}
          icon={data.rationale_icon}
        />
      </View>
      <View wrap={false}>
        <SectionBlock
          title="Recommendation"
          items={data.recommendation}
          icon={data.reccomendation_icon}
        />
      </View>
      <View wrap={false}>
        <SectionBlock
          title="Expected Outcome with Autopilot"
          items={data?.expected_outcome}
          icon={data.reccomendation_icon}
        />
      </View>
    </>
  );
};

EWIDataInLightBlueCard.propTypes = {
  data: PropTypes.object,
};

export const SectionBlockActionPlan = ({ title, items, icon }) => (
  <View style={[PDFstyles.lightBlueCard, { backgroundColor: "#F0F2FC" }]}>
    {/* Header */}
    <View
      style={{ flexDirection: "row", alignItems: "center", marginBottom: 16 }}
    >
      <View style={{ flex: 1 }}>
        <Text style={[PDFstyles.pageOneCardSubTitle, { marginBottom: 0 }]}>
          {title}
        </Text>
      </View>
      <Image src={icon} style={{ height: 32, width: 32 }} />
    </View>

    {/* Content */}
    {(Array.isArray(items) ? items : [items]).map((point, index) => {
      const raw = cleanText(String(point).trim());

      const isBullet = raw.startsWith("- ");
      const content = isBullet ? raw.replace(/^-+\s*/, "") : raw;

      // split by if present
      const lines = content.split(/<br\s*\/?>|<\/br>/gi);

      return (
        <View
          key={"section"}
          style={{
            flexDirection: "row",
            marginBottom: 6, // controlled spacing
          }}
        >
          {/* ✅ Render bullet ONLY ONCE */}
          {isBullet && <Text style={{ marginRight: 6 }}>•</Text>}

          {/* ✅ Single flowing text block */}
          <Text
            style={PDFstyles.EWICardData}
            hyphenationCallback={hyphenationCallback}
          >
            {lines.map((line, lineIndex) => {
              const parts = line.split(/(\*\*.*?\*\*)/g).filter(Boolean);

              return (
                <Text key={line}>
                  {parts.map((part, i) => {
                    const isBold = part.startsWith("**") && part.endsWith("**");

                    const text = isBold
                      ? part.replaceAll(/^\*\*|\*\*$/g, "")
                      : part;

                    return (
                      <Text
                        key={`${line}-${part}`}
                        style={{
                          fontFamily: "Poppins",
                          fontWeight: isBold ? 700 : 400,
                        }}
                      >
                        {breakLongUrl(cleanText(text))}
                      </Text>
                    );
                  })}

                  {/* ✅ controlled line break ONLY between lines */}
                  {lineIndex < lines.length - 1 ? "\n" : ""}
                </Text>
              );
            })}
          </Text>
        </View>
      );
    })}
  </View>
);



SectionBlockActionPlan.propTypes = {
  title: PropTypes.string,
  items: PropTypes.array,
  icon: PropTypes.node,
};

export const EWIDataInActionPlan = ({ data, pageStyle, footerStyle }) => {
  if (!data) return null;

  return (
    <>
      {/* Page 1 */}
      <Page size="LETTER" style={pageStyle}>
        <Text style={[PDFstyles.pageOneCardSubTitle, { marginBottom: 13 }]}>
          Detailed Action Plan
        </Text>

        <SectionBlock
          title="Problem"
          items={data.problem}
          icon={data.problem_icon}
        />

        <PDFFooter style={footerStyle} />
      </Page>

      {/* Page 2 */}
      <Page size="LETTER" style={pageStyle}>
        <SectionBlock
          title="Consequence"
          items={data.rationale}
          icon={data.rationale_icon}
        />

        <PDFFooter style={footerStyle} />
      </Page>

      {/* Page 3 */}
      <Page size="LETTER" style={pageStyle}>
        <SectionBlockActionPlan
          title="Short-Term (0–6 months)"
          items={data.detailed_action_plan.short_term}
          icon={data.short_term_icon}
        />

        <PDFFooter style={footerStyle} />
      </Page>

      {/* Page 4 */}
      <Page size="LETTER" style={pageStyle}>
        <SectionBlockActionPlan
          title="Mid-Term (6–12 months)"
          items={data.detailed_action_plan.mid_term}
          icon={data.mid_term_icon}
        />

        <PDFFooter style={footerStyle} />
      </Page>

      {/* Page 5 */}
      <Page size="LETTER" style={pageStyle}>
        <SectionBlockActionPlan
          title="Long-Term (12+ months)"
          items={data.detailed_action_plan.long_term}
          icon={data.long_term_icon}
        />

        <PDFFooter style={footerStyle} />
      </Page>
    </>
  );
};

EWIDataInActionPlan.propTypes = {
  data: PropTypes.object,
  pageStyle: PropTypes.object,
  footerStyle: PropTypes.object,
};

const FinancialHealthScoreDetailsPDF = ({
  orgName,
  selectedQuater,
  base64Chart,
  base64ChartNII,
  selectedbankMetrics,
  base64Logo,
  base64Banner,
  formattedDate,
  earlyWarningIndicatorsObjPdf,
  segmentInfluencingObjPdf,
  allSegmentDataObjPdf,
  regulatorySegmentsDataObjPdf,
  showAPValue,
}) => {
  const segmentInfluencingObj = segmentInfluencingObjPdf;

  const allSegments = allSegmentDataObjPdf;

  const EWIdataArr = earlyWarningIndicatorsObjPdf;

  const regulatorySegments = regulatorySegmentsDataObjPdf;

  return (
    <Document>
      <Page size="LETTER">
        <MainPage
          base64Logo={base64Logo}
          base64Banner={base64Banner}
          orgName={orgName}
          formattedDate={formattedDate}
          selectedQuater={selectedQuater}
        />
      </Page>

      <Page size="LETTER" style={PDFstyles.insidePage}>
        <FHSGuageChart base64Chart={base64Chart} orgName={orgName} />

        <NIIGraph base64ChartNII={base64ChartNII} />

        <SegmentInfluence
          segmentInfluencingObj={segmentInfluencingObj}
          selectedbankMetrics={selectedbankMetrics}
          showAPValue={showAPValue}
        />

        <AllSegment
          allSegments={allSegments}
          selectedbankMetrics={selectedbankMetrics}
          showAPValue={showAPValue}
        />

        <PDFFooter style={PDFstyles.pageFooter} />
      </Page>

      <Page size="LETTER" style={PDFstyles.insidePage}>
        <RegulatorySegment
          regulatorySegments={regulatorySegments}
          selectedbankMetrics={selectedbankMetrics}
          showAPValue={showAPValue}
        />

        <PDFFooter style={PDFstyles.pageFooter} />
      </Page>

      <EWIData EWIdataArr={EWIdataArr} />
    </Document>
  );
};

FinancialHealthScoreDetailsPDF.propTypes = {
  orgName: PropTypes.string,
  selectedQuater: PropTypes.string,
  base64Chart: PropTypes.string,
  base64ChartNII: PropTypes.string,
  selectedbankMetrics: PropTypes.arrayOf(PropTypes.object),
  base64Logo: PropTypes.string,
  base64Banner: PropTypes.string,
  formattedDate: PropTypes.string,
  earlyWarningIndicatorsObjPdf: PropTypes.arrayOf(PropTypes.object),
  segmentInfluencingObjPdf: PropTypes.arrayOf(PropTypes.object),
  allSegmentDataObjPdf: PropTypes.arrayOf(PropTypes.object),
  regulatorySegmentsDataObjPdf: PropTypes.arrayOf(PropTypes.object),
  showAPValue: PropTypes.bool,
};

export default FinancialHealthScoreDetailsPDF;
